import { h, render } from 'vue'
import ToastMessage from '../components/molecules/ToastMessage.vue'

type ToastType = 'error' | 'success' | 'warning' | 'info'
type Politeness = 'polite' | 'assertive'

interface ToastOptions {
  type?: ToastType
  title?: string
  message: string
  duration?: number
}

// JAWS and NVDA skip a live region that is inserted already holding text
// (FreedomScientific/standards-support#696), so a message lands in a later task than its
// region. 100ms follows react-aria, which found shorter delays unreliable in Safari.
const ANNOUNCE_DELAY_MS = 100

const liveRegion = (politeness: Politeness) => {
  const id = `plt-toast-live-${politeness}`
  const existing = document.getElementById(id)
  if (existing) return existing

  const region = document.createElement('div')
  region.id = id
  // Not role="status"/"alert": both imply aria-atomic, which re-reads every message
  // still in the region whenever another is added.
  region.setAttribute('role', 'log')
  region.setAttribute('aria-live', politeness)
  region.setAttribute('aria-relevant', 'additions')
  Object.assign(region.style, {
    position: 'absolute',
    width: '1px',
    height: '1px',
    margin: '-1px',
    padding: '0',
    border: '0',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap',
  })
  document.body.appendChild(region)
  return region
}

// Singleton toast manager to prevent memory leaks
class ToastManager {
  private toasts = new Set<HTMLElement>()

  addToast(container: HTMLElement) {
    this.toasts.add(container)
  }

  removeToast(container: HTMLElement) {
    this.toasts.delete(container)
    if (container.parentNode) {
      container.parentNode.removeChild(container)
    }
  }

  cleanup() {
    this.toasts.forEach(container => {
      if (container.parentNode) {
        container.parentNode.removeChild(container)
      }
    })
    this.toasts.clear()
  }
}

const toastManager = new ToastManager()

/**
 * Toast notification composable
 *
 * Provides methods to display temporary notification messages with auto-dismiss.
 * Manages toast lifecycle and prevents memory leaks with singleton pattern.
 * Announces each message to screen readers: errors assertively, other types politely.
 *
 * @example Basic usage
 * ```typescript
 * const toast = useToast()
 *
 * // Show success message
 * toast.showToast({
 *   type: 'success',
 *   message: 'Changes saved successfully!'
 * })
 * ```
 *
 * @example With title and custom duration
 * ```typescript
 * toast.showToast({
 *   type: 'error',
 *   title: 'Upload Failed',
 *   message: 'The file size exceeds the maximum limit',
 *   duration: 5000
 * })
 * ```
 *
 * @example In async operations
 * ```typescript
 * async function saveData() {
 *   const toast = useToast()
 *   try {
 *     await api.save(data)
 *     toast.showToast({
 *       type: 'success',
 *       message: 'Data saved successfully'
 *     })
 *   } catch (error) {
 *     toast.showToast({
 *       type: 'error',
 *       message: error.message
 *     })
 *   }
 * }
 * ```
 *
 * @returns Object with showToast method
 * @see {@link packages/ui-vue/src/composables/useToast.ts:1}
 */
export function useToast() {
  const showToast = ({ type = 'info', title, message, duration = 3000 }: ToastOptions) => {
    const region = liveRegion(type === 'error' ? 'assertive' : 'polite')
    const announcement = document.createElement('div')
    announcement.textContent = title ? `${title} ${message}` : message
    setTimeout(() => region.appendChild(announcement), ANNOUNCE_DELAY_MS)

    // Create a container for the toast
    const container = document.createElement('div')
    // Screen readers get the announcement; without this, browse mode finds the text twice.
    container.setAttribute('aria-hidden', 'true')
    document.body.appendChild(container)

    // Create the toast component
    const vnode = h(ToastMessage, {
      type,
      title,
      message,
      duration,
      onUnmount: () => {
        // Remove the container after animation
        setTimeout(() => {
          toastManager.removeToast(container)
          announcement.remove()
        }, 300) // Wait for animation to complete
      },
    })

    // Render the toast
    render(vnode, container)
    toastManager.addToast(container)
  }

  return {
    showToast,
  }
}
